import { useEffect, useState } from "react";
import { Captions, Mic } from "lucide-react";
import { useApp } from "@/lib/i18n";
import { getDemo } from "./demoData";

const BARS = Array.from({ length: 48 }, (_, i) => ({ d: (0.7 + ((i * 37) % 9) / 10).toFixed(2), delay: ((i * 53) % 17) / 20, h: 30 + ((i * 29) % 70) }));
const pad = (n) => String(n).padStart(2, "0");

const useDemo = (SCRIPT) => {
  const [line, setLine] = useState(0);
  const [words, setWords] = useState(0);
  const [sec, setSec] = useState(0);
  useEffect(() => {
    const total = SCRIPT[line][1].split(" ").length;
    const t = setTimeout(() => {
      if (words < total) setWords(words + 1);
      else { setLine((line + 1) % SCRIPT.length); setWords(0); }
    }, words < total ? 230 : 1600);
    return () => clearTimeout(t);
  }, [line, words, SCRIPT]);
  useEffect(() => { const t = setInterval(() => setSec((s) => s + 1), 1000); return () => clearInterval(t); }, []);
  return { line, words, sec };
};

export const LiveDemo = () => {
  const { L, lang } = useApp();
  const SCRIPT = getDemo(lang).live;
  const { line, words, sec } = useDemo(SCRIPT);
  const [speaker, text] = SCRIPT[line];
  const prev = line > 0 ? SCRIPT[line - 1] : null;
  return (
    <section className="py-16 md:py-24" data-testid="landing-live-demo">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <p className="text-xs font-bold uppercase tracking-wider text-violet-700">{L("Transkripsi langsung", "Live transcription")}</p>
        <h2 className="mt-3 max-w-3xl text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{L("Lihat setiap perkataan muncul semasa anda bercakap.", "Watch every word appear as you speak.")}</h2>
        <div className="mt-10 overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-violet-600/5">
          <div className="border-b border-slate-100 p-5 sm:p-8">
            <div className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-3 font-semibold text-slate-700">
                <span className="rec-pulse grid h-9 w-9 place-items-center rounded-full bg-red-50"><span className="h-3.5 w-3.5 rounded-full bg-red-500" /></span>
                {L("Sedang merakam…", "Recording…")}
              </span>
              <span className="font-mono text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl" data-testid="demo-timer">{pad(Math.floor(sec / 60) % 60)}:{pad(sec % 60)}</span>
            </div>
            <div className="mt-6 flex h-24 items-center gap-[3px] rounded-2xl bg-slate-50 px-3 sm:h-32 sm:gap-1.5 sm:px-5" aria-hidden data-testid="demo-wave">
              {BARS.map((b, i) => (
                <span key={i} className={`wave-bar flex-1 rounded-sm bg-violet-600 ${i % 2 ? "hidden sm:block" : ""}`}
                  style={{ height: `${b.h}%`, animationDuration: `${b.d}s`, animationDelay: `-${b.delay}s` }} />
              ))}
            </div>
          </div>
          <div className="p-5 sm:p-8">
            <div className="flex flex-wrap items-center gap-3">
              <Captions size={18} className="text-violet-600" />
              <span className="font-semibold">{L("Transkripsi langsung", "Live transcription")}</span>
              <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold tracking-wider text-red-600"><span className="h-1.5 w-1.5 rounded-full bg-red-500" /> {L("LANGSUNG", "LIVE")}</span>
              <span className="rounded-full bg-violet-50 px-2.5 py-1 text-xs font-semibold text-violet-700">{L("Bahasa Melayu · penutur dikenal pasti", "English · speakers identified")}</span>
            </div>
            <div className="mt-6 min-h-[13rem] space-y-5 sm:min-h-[15rem]" data-testid="demo-transcript">
              {prev && (
                <p key={`p${line}`} className="live-final flex gap-4 text-lg text-slate-400 sm:text-xl">
                  <span className="w-24 shrink-0 pt-1 text-xs font-bold uppercase tracking-wider text-slate-400">{prev[0]}</span>
                  <span className="line-clamp-2">{prev[1]}</span>
                </p>
              )}
              <p className="flex gap-4">
                <span className="flex w-24 shrink-0 flex-col pt-2 text-xs font-bold uppercase tracking-wider text-violet-600"><Mic size={13} className="mb-1" />{speaker}</span>
                <span className="text-2xl font-medium leading-snug text-slate-900 sm:text-3xl lg:text-4xl lg:leading-snug" data-testid="demo-live-text">
                  {text.split(" ").slice(0, words).map((w, i) => <span key={`${line}-${i}`} className="live-word mr-[0.28em]">{w}</span>)}
                  <span className="live-caret" />
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
